import { t } from "@lingui/macro";
import { Tooltip } from "@visx/tooltip";
import { defaultStyles } from "@visx/tooltip/lib/tooltips/Tooltip";
import React, { useEffect, useState } from "react";

const tooltipStyles = {
  ...defaultStyles,
  minWidth: 140,
  backgroundColor: "rgba(0,0,0,0.9)",
  color: "white",
  padding: "10px 14px",
  borderRadius: "4px",
  boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
  fontSize: "14px",
};

const formatPercentage = (value: number): string => {
  return `${Math.round(value * 100)}%`;
};

type MetricScoreTooltipProps = {
  fullName: string;
  score: number;
  importance: number;
  description: string;
  top: number;
  left: number;
  open: boolean;
};

export const MetricScoreTooltip: React.FC<MetricScoreTooltipProps> = ({
  fullName,
  score,
  importance,
  description,
  top,
  left,
  open,
}) => {
  const [animatedScore, setAnimatedScore] = useState(0);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    if (open && score != null) {
      setPulse(false);
      const end = score;
      const start = Math.max(0, end - 0.10);
      const duration = 700;
      const startTime = performance.now();
      let frameId: number;

      function easeOutCubic(t: number) {
        return 1 - Math.pow(1 - t, 3);
      }

      const animate = (now: number) => {
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = easeOutCubic(progress);
        const current = start + (end - start) * eased;
        setAnimatedScore(current);
        if (progress < 1) {
          frameId = requestAnimationFrame(animate);
        } else {
          setAnimatedScore(end);
          setPulse(true);
        }
      };
      setAnimatedScore(start);
      frameId = requestAnimationFrame(animate);
      return () => {
        cancelAnimationFrame(frameId);
      };
    } else {
      setAnimatedScore(0);
      setPulse(false);
    }
  }, [open, score]);

  if (!open) return null;
  return (
    <Tooltip
      className="rounded-lg! bg-black/55! backdrop-blur-lg"
      top={top}
      left={left}
      style={tooltipStyles}
    >
      <div className="space-y-1">
        <strong className="block text-md font-black border-b border-b-primary/25 pb-2 border-dashed">{fullName}</strong>
        <div className="flex items-center justify-between mb-1 gap-x-2"> 
          <span className="block text-sm font-bold text-teal-300">
            {t`Score:`} <span className={`text-white text-md bg-teal-300/25 px-2 py-0.5 inline-block rounded-md${pulse ? ' animate-scale-pop' : ''}`}>{formatPercentage(animatedScore)}</span>
          </span>
          <span className="block text-xs text-yellow-300">
            {t`Importance:`} {formatPercentage(importance)}
          </span>
        </div>
        <span className="mt-1 block max-w-xs text-xs text-slate-300">
          {description}
        </span>
      </div>
    </Tooltip>
  );
}; 